import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Buildings, FloppyDisk, Upload as UploadIcon, BellSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = {
  name: "", address: "", phone: "", email: "", website: "",
  gstin: "", pan: "", state_code: "",
  logo_data_url: "",
  bank_name: "", bank_account: "", bank_ifsc: "",
  invoice_footer: "", terms_conditions: "",
};

export default function CompanyProfile() {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [notif, setNotif] = useState(null); // { twilio_enabled, twilio_configured, whatsapp_configured, twilio_source }
  const [notifSaving, setNotifSaving] = useState(false);

  const load = async () => {
    const { data } = await http.get("/company/profile");
    setForm({ ...empty, ...data });
    try { const { data: s } = await http.get("/settings"); setNotif(s); } catch {}
  };
  useEffect(() => { load(); }, []);

  const toggleTwilio = async (next) => {
    setNotifSaving(true);
    try {
      await http.post("/settings/notifications", { twilio_enabled: next });
      setNotif((n) => ({ ...(n || {}), twilio_enabled: next, twilio_source: "db" }));
      toast.success(next ? "WhatsApp/SMS alerts resumed" : "WhatsApp/SMS alerts muted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toggle failed");
    } finally { setNotifSaving(false); }
  };

  const onLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 500 * 1024) { toast.error("Logo must be under 500 KB"); return; }
    const r = new FileReader();
    r.onload = () => setForm({ ...form, logo_data_url: String(r.result) });
    r.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await http.put("/company/profile", form);
      toast.success("Company profile saved — will appear on new invoices");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 fade-up max-w-4xl" data-testid="company-view">
      <div>
        <div className="uppercase-label">Settings</div>
        <h1 className="font-display font-bold text-4xl mt-1 flex items-center gap-3"><Buildings size={28} className="text-primary" /> Company profile</h1>
        <p className="text-sm text-muted-foreground mt-2">This block appears on every invoice PDF. GSTIN + State-code drive CGST/SGST vs IGST split.</p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <Section title="Identity">
          <div className="grid grid-cols-2 gap-4">
            <F label="Company name" span={2}><Input required data-testid="cp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Address" span={2}><Textarea data-testid="cp-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} /></F>
            <F label="Phone"><Input data-testid="cp-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
            <F label="Email"><Input type="email" data-testid="cp-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
            <F label="Website" span={2}><Input data-testid="cp-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></F>
            <F label="Logo (PNG/JPG, max 500 KB)" span={2}>
              <div className="flex items-center gap-3">
                {form.logo_data_url && <img src={form.logo_data_url} alt="logo" className="w-14 h-14 object-contain border border-border bg-white p-1" />}
                <label className="cursor-pointer inline-flex items-center gap-2 border border-border px-3 py-2 text-sm hover:bg-accent">
                  <UploadIcon size={14} /> Upload logo
                  <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="cp-logo" />
                </label>
              </div>
            </F>
          </div>
        </Section>

        <Section title="Tax details">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <F label="GSTIN"><Input data-testid="cp-gstin" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="29ABCDE1234F1Z5" className="font-mono" /></F>
            <F label="PAN"><Input data-testid="cp-pan" value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" className="font-mono" /></F>
            <F label="State code (2-digit)"><Input data-testid="cp-state" maxLength={2} value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })} placeholder="29" className="font-mono" /></F>
          </div>
        </Section>

        <Section title="Bank details (printed on invoice)">
          <div className="grid grid-cols-3 gap-4">
            <F label="Bank name"><Input data-testid="cp-bank-name" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></F>
            <F label="Account number"><Input data-testid="cp-bank-ac" value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} className="font-mono" /></F>
            <F label="IFSC"><Input data-testid="cp-bank-ifsc" value={form.bank_ifsc} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })} className="font-mono" /></F>
          </div>
        </Section>

        <Section title="Invoice footer & terms">
          <div className="grid grid-cols-1 gap-4">
            <F label="Footer line (small italic under bank details)"><Input data-testid="cp-footer" value={form.invoice_footer} onChange={(e) => setForm({ ...form, invoice_footer: e.target.value })} placeholder="Thank you for your business" /></F>
            <F label="Terms & Conditions"><Textarea rows={3} data-testid="cp-terms" value={form.terms_conditions} onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })} placeholder="Payment due within 15 days. Late fee 2% per month." /></F>
          </div>
        </Section>

        <Section title="Notifications">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">
                <BellSlash size={16} className="text-primary" />
                WhatsApp / SMS alerts
              </div>
              <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                Mute every outbound alert fan-out (invoice reminders, disk-watchdog, future RX-breach pages) without rotating credentials. Takes effect within 10 seconds — no SSH or container restart needed.
              </p>
              <div className="text-[10px] font-mono text-muted-foreground mt-2">
                {notif?.twilio_configured
                  ? <>Twilio provider: <span className="text-green-500">configured</span></>
                  : <>Twilio provider: <span className="text-amber-500">not configured</span> — add creds in /opt/sns-radius/backend/.env</>}
                {notif?.twilio_source === "db" && <span className="ml-2">· source: UI override</span>}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className={`text-xs font-mono ${notif?.twilio_enabled ? "text-green-500" : "text-muted-foreground"}`}>
                {notif?.twilio_enabled ? "ENABLED" : "MUTED"}
              </span>
              <Switch
                data-testid="notif-twilio-toggle"
                checked={!!notif?.twilio_enabled}
                disabled={notifSaving || notif === null}
                onCheckedChange={toggleTwilio}
              />
            </div>
          </div>
        </Section>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} data-testid="cp-save" className="rounded-sm bg-primary hover:bg-primary/90">
            <FloppyDisk size={14} className="mr-2" /> {saving ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </form>
    </div>
  );
}

const F = ({ label, span = 1, children }) => (
  <div className={span === 2 ? "col-span-2" : span === 3 ? "col-span-3" : ""}>
    <Label className="uppercase-label">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="border border-border bg-card p-5">
    <div className="uppercase-label mb-4">{title}</div>
    {children}
  </div>
);
